import type { LotteryBlock } from '@todo/shared';
import { formatLotteryCell } from '../../lib/lottery-format';
import { downloadXlsx } from '../../lib/xlsx';

type TableBlock = Extract<LotteryBlock, { type: 'table' }>;

const NUMERIC = new Set(['count', 'money', 'pct1', 'pct2', 'int']);

/**
 * A table of a Lottery analyst answer (Phase 8). The rows are the REAL
 * query rows the server stored; every value is formatted here through
 * lib/lottery-format (Indian grouping, ₹, %). A header that stays put, and
 * a .xlsx download of exactly what is shown.
 */
export function LotteryChatTable({ block }: { block: TableBlock }) {
  const download = () => {
    downloadXlsx(
      (block.title || 'lottery-table').replace(/[^\w -]+/g, '').slice(0, 60) || 'lottery-table',
      block.title || 'Table',
      [block.columns.map((c) => c.label), ...block.rows.map((r) => block.columns.map((c) => formatLotteryCell(r[c.key], c.kind)))],
      undefined,
      { freezeHeader: true },
    );
  };

  return (
    <figure className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <figcaption className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-2.5 py-1.5">
        <span className="text-xs font-semibold text-slate-700">{block.title}</span>
        <button type="button" onClick={download} className="rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-50">
          Download .xlsx
        </button>
      </figcaption>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-100 text-slate-600">
            <tr>
              {block.columns.map((c) => (
                <th key={c.key} scope="col" className={`whitespace-nowrap px-2 py-1 font-medium ${NUMERIC.has(c.kind) ? 'text-right' : 'text-left'}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 odd:bg-white even:bg-slate-50/60">
                {block.columns.map((c) => (
                  <td key={c.key} className={`whitespace-nowrap px-2 py-1 text-slate-800 ${NUMERIC.has(c.kind) ? 'text-right tabular-nums' : ''}`}>
                    {formatLotteryCell(r[c.key], c.kind)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {block.truncated && (
        <p className="border-t border-slate-200 px-2.5 py-1 text-[11px] text-slate-500">
          Showing {block.rows.length} of {block.totalRows} rows. Ask for an Excel report for all of them.
        </p>
      )}
    </figure>
  );
}
